import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const SupportSettingsTab = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['support-contact-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_contacts')
        .select('id, email, phone')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (data) {
      setEmail(data.email || '');
      setPhone(data.phone || '');
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!data?.id) throw new Error('No support contact row');
      const { error } = await supabase
        .from('support_contacts')
        .update({ email, phone })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('common.success'));
      queryClient.invalidateQueries({ queryKey: ['support-contact'] });
      queryClient.invalidateQueries({ queryKey: ['support-contact-admin'] });
    },
    onError: (err: any) => toast.error(err.message || t('common.error')),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('support.adminTitle')}</CardTitle>
        <CardDescription>{t('support.adminDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Loader2 className="animate-spin" />
        ) : (
          <>
            <div className="space-y-2">
              <Label>{t('support.email')}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@myvelox.eu"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('support.phone')}</Label>
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+32 ..."
              />
            </div>
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748]"
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
